import { Feather, AudioLines, Leaf, type LucideIcon } from 'lucide-react'

export type BlogCategory =
  | 'soul-thoughts'
  | 'energy-tuning'
  | 'lifestyle-basket'

export type ContentBlock =
  | { type: 'paragraph'; text: string }
  | { type: 'heading'; text: string }
  | { type: 'list'; ordered?: boolean; items: string[] }
  | { type: 'quote'; text: string; author?: string }

export interface BlogPost {
  slug: string
  title: string
  excerpt: string
  category: BlogCategory
  date: string
  image: string
  content: ContentBlock[]
  /** Original source reference — no longer used for navigation. */
  externalUrl?: string
}

export interface Category {
  id: BlogCategory
  name: string
  description: string
  icon: LucideIcon
}

export const categories: Category[] = [
  {
    id: 'soul-thoughts',
    name: 'Soul Thoughts',
    description: 'Personal reflections, mindful living, inner journeys.',
    icon: Feather,
  },
  {
    id: 'energy-tuning',
    name: 'Energy Tuning & Meditation',
    description:
      'Biofield Tuning, sound work, meditation practices, and insights on energy.',
    icon: AudioLines,
  },
  {
    id: 'lifestyle-basket',
    name: 'Lifestyle Basket',
    description: 'Simple joys of nature, nourishment, and creativity.',
    icon: Leaf,
  },
]

// Fallback used when an image fails to load
export const FALLBACK_IMAGE =
  'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?auto=format&fit=crop&q=80'

export const blogPosts: BlogPost[] = [
  // Lifestyle Basket
  {
    slug: 'healing-powers-of-garlic',
    title: 'The healing powers of garlic',
    excerpt:
      "A humble bulb with extraordinary gifts. Exploring garlic's ancient role as a natural healer — from immunity to vitality, the simple medicines that grow in our own kitchens.",
    category: 'lifestyle-basket',
    date: '2023-08-24',
    image:
      'https://cdn.magicpatterns.com/uploads/ssXA1xckY3LRbAnmSgbgNq/garlic.png',
    externalUrl:
      'https://milangelassoulgarden.com/2023/08/24/healing-powers-of-garlic/',
    content: [
      {
        type: 'paragraph',
        text: 'In ancient and medieval times, garlic was prized for its medicinal properties, and despite its notorious aroma and flavor it has remained highly appreciated for its strong healing effects and that delicious taste on the palate.',
      },
      { type: 'paragraph', text: 'But what is it good for?' },
      {
        type: 'list',
        ordered: true,
        items: [
          'It helps bust your immune system. Apparently, two cloves of garlic a day suffice. Some nations still have their children wear a string of garlic cloves around their necks to keep them healthy. No laughing matter! Peeled garlic cut open releases allicin that penetrates the skin and gets into the bloodstream easier and faster than when consumed.',
          'It reduces high blood pressure and cholesterol levels.',
          'It can help with the prevention of cancer.',
          'It is very nutritious, yet low in calories but rich in vitamin C, B6, manganese, selenium, and fiber.',
        ],
      },
    ],
  },
  {
    slug: 'spending-time-in-forests-shinrin-yoku',
    title: 'Spending time in forests — Shinrin-yoku',
    excerpt:
      'The Japanese practice of forest bathing as medicine for body and soul. How simply being among the trees restores the nervous system and reconnects us to something larger.',
    category: 'lifestyle-basket',
    date: '2023-04-15',
    image:
      'https://images.unsplash.com/photo-1448375240586-882707db888b?auto=format&fit=crop&q=80&w=1200',
    externalUrl:
      'https://milangelassoulgarden.com/2023/04/15/spending-time-in-forests-shinrin-joku/',
    content: [
      {
        type: 'paragraph',
        text: "Spending time around trees, forgetting busy life, and mindfully observing nature and its wonders. Of course! Tell me about it! And yet, for several reasons, people have been turning away from nature's ways.",
      },
      { type: 'heading', text: 'Life in the city' },
      {
        type: 'paragraph',
        text: 'As a former city dweller, I do understand the busyness of life and time scarcity. But never have I said no to being surrounded by greenery. Even during days when having plants meant carrying all those heavy bags of soil and plant pots to a flat on the fourth floor. Even then, our terrace was a paradise of flowers, vegetables, and herbs. A true feast for the eyes and nose, with its colorful shades, floral scents, aromas, and fragrances.',
      },
      {
        type: 'paragraph',
        text: 'Watching me during my urban gardening for some time, my husband one day declared: “We need to move to the countryside.”',
      },
    ],
  },

  // Soul Thoughts
  {
    slug: 'how-to-move-on-when-life-has-pulled-you-down',
    title: 'How to move on when life has pulled you down',
    excerpt:
      'There are seasons that ask everything of us. A gentle exploration of resilience, surrender, and the quiet courage it takes to keep walking forward.',
    category: 'soul-thoughts',
    date: '2023-06-14',
    image:
      'https://images.unsplash.com/photo-1499209974431-9dddcece7f88?auto=format&fit=crop&q=80&w=1200',
    externalUrl:
      'https://milangelassoulgarden.com/2023/06/14/how-to-move-on-when-life-has-pulled-you-down/',
    content: [
      {
        type: 'paragraph',
        text: 'Overcoming tough times can be challenging, but with the right mindset and strategies, you can navigate through them successfully. Here are some steps to help you overcome tough times:',
      },
      {
        type: 'list',
        ordered: true,
        items: [
          "Accept your emotions: It's normal to experience a range of emotions during difficult times, such as sadness, anger, or frustration. Acknowledge and accept these emotions, allowing yourself to feel them without judgment.",
          'Seek support: Reach out to friends, family, or a support network for emotional support. Talking to someone you trust can provide comfort, perspective, and advice. If needed, consider seeking professional help from a therapist or counselor.',
          'Practice self-care: Take care of yourself physically, mentally, and emotionally. Get enough sleep, eat nourishing meals, engage in regular exercise, and make time for activities that bring you joy and relaxation. Self-care is crucial for maintaining wellbeing during tough times.',
        ],
      },
    ],
  },
  {
    slug: 'who-are-we-in-reality',
    title: 'Who are we in reality?',
    excerpt:
      'Beneath the roles we play and the stories we carry, who is the one observing it all? Reflections on identity, presence, and the deeper self.',
    category: 'soul-thoughts',
    date: '2023-02-28',
    image:
      'https://images.unsplash.com/photo-1502082553048-f009c37129b9?auto=format&fit=crop&q=80&w=1200',
    externalUrl:
      'https://milangelassoulgarden.com/2023/02/28/who-are-we-in-reality/',
    content: [
      {
        type: 'paragraph',
        text: 'In recent years, perhaps more than before, I have been increasingly asking myself the question of who I am and what my purpose here on Earth is. Why are some people doing well – or so it seems – and why do many struggle – hardly making both ends meet?',
      },
      {
        type: 'paragraph',
        text: 'I used to believe in fate but no longer do. I have long since stopped giving any credence to the idea that nothing can be changed, and, honestly, find such deterministic paradigms not only antiquated but truly misleading.',
      },
      {
        type: 'paragraph',
        text: 'As I silently watch this world go round and round, spinning us like kids on a merry-go-round, I wonder: ‘Why? Why in this “apparel”? Why carry the physical body around when most likely what really counts is the invisible field enveloping our bodies? What is there to learn in this strenuous, physical form? What is the purpose of dragging not just our bodies, but also all the burdens we add on during our journey?’',
      },
    ],
  },
  {
    slug: 'on-life',
    title: 'On life',
    excerpt:
      'A meditation on the ordinary miracle of being here. Small notes on what it means to truly live — not just exist — within the days we are given.',
    category: 'soul-thoughts',
    date: '2023-01-22',
    image:
      'https://images.unsplash.com/photo-1470770841072-f978cf4d019e?auto=format&fit=crop&q=80&w=1200',
    externalUrl: 'https://milangelassoulgarden.com/2023/01/22/on-life/',
    content: [
      { type: 'paragraph', text: 'Feel the hurt.' },
      { type: 'paragraph', text: 'Shed your tears.' },
      { type: 'paragraph', text: 'Do the work.' },
      { type: 'paragraph', text: 'Heal, and grow spiritually!' },
      {
        type: 'quote',
        text: 'Heal, and grow spiritually.',
        author: 'Milangela',
      },
    ],
  },
  {
    slug: 'on-new-years-resolutions',
    title: "On New Year's resolutions",
    excerpt:
      'Beyond the lists and ambitions — what if the new year invited a softer kind of intention? Thoughts on beginnings that honor where we actually are.',
    category: 'soul-thoughts',
    date: '2022-12-30',
    image:
      'https://images.unsplash.com/photo-1483794344563-d27a8d18014e?auto=format&fit=crop&q=80&w=1200',
    externalUrl:
      'https://milangelassoulgarden.com/2022/12/30/on-new-years-resolutions/',
    content: [
      {
        type: 'paragraph',
        text: "I remember the times when I was thrilled about the magic that Christmas and New Year's Eve brought about. Like every kid around the globe, I believed in the magic of Santa Claus. Making a list was a big chore done ever so carefully. But once it had been written, nothing could go wrong. Well, of course, playing by the rule »be good« was key. But that never seemed hard to do. I was doing my best and truly visualized the white-bearded man and the reindeer circling the rooftops, tossing presents down the chimneys. Nothing else mattered – the magical time and the excitement and expectation. I was full of fervent hope that all my wishes be granted and for the most part, they were. Moderate living did not leave much room for big dreams. When presents finally appeared my heart was beating like crazy and before unwrapping them I used to hold them in my arms, pressing them against my heart, in appreciation, I suppose. It was like being under a spell. I treasured everything that appeared in front of my eyes, as I was unwrapping the parcel. That was then and we were kids.",
      },
      {
        type: 'list',
        ordered: true,
        items: ['Pay attention to what matters in life.'],
      },
    ],
  },
  {
    slug: 'appreciating-what-matters',
    title: 'Appreciating what matters',
    excerpt:
      'When we pause long enough to notice, the most important things have often been here all along. A reflection on gratitude as a way of seeing.',
    category: 'soul-thoughts',
    date: '2022-11-21',
    image:
      'https://images.unsplash.com/photo-1418065460487-3e41a6c84dc5?auto=format&fit=crop&q=80&w=1200',
    externalUrl:
      'https://milangelassoulgarden.com/2022/11/21/appreciating-what-matters/',
    content: [
      {
        type: 'paragraph',
        text: 'How long does it take us to enjoy what we have, to be grateful for this moment?',
      },
      {
        type: 'paragraph',
        text: 'Why are we blind to the “riches & happiness” of what the simplest of things hide within?',
      },
      {
        type: 'paragraph',
        text: 'Why do we constantly crave “more”, “better”, and “higher” and sap our vital energy till we can do no more?',
      },
      {
        type: 'paragraph',
        text: 'I am certain the powerful sentence below will keep us busy all month.',
      },
    ],
  },
  {
    slug: 'the-mighty-silence',
    title: 'The mighty silence',
    excerpt:
      'Silence is not absence — it is its own kind of fullness. On the quiet that holds us, teaches us, and reveals what noise cannot.',
    category: 'soul-thoughts',
    date: '2022-09-15',
    image:
      'https://images.unsplash.com/photo-1518495973542-4542c06a5843?auto=format&fit=crop&q=80&w=1200',
    externalUrl:
      'https://milangelassoulgarden.com/2022/09/15/the-mighty-silence/',
    content: [
      {
        type: 'paragraph',
        text: 'My good moments in meditation, those powerful, peaceful, captivating times when I just AM – silent, absorbed, content, enough – have made me understand the significance of »being«. The magical moments of peace and joy in my soul. Only then can I see the ways of the world as they are. A mere observer, emotionally detached, not allowing myself to get caught in invisible traps. How wonderful it is when we can refrain from comments.',
      },
      {
        type: 'paragraph',
        text: "Silence has enormous power. Yet, for the most part, in my view, people are scared of it. So they talk only to break it. Think of times when someone, say the speaker of an event, took the liberty of not saying anything for a minute. Everyone present is startled and starts to look around, instead of leaning into the space, enjoying the moment, and getting prepared for what's coming.",
      },
      { type: 'paragraph', text: 'Blissful silence.' },
    ],
  },
  {
    slug: 'the-power-of-thought',
    title: 'The power of thought',
    excerpt:
      'Our inner conversation shapes the world we inhabit. A gentle look at how thoughts become beliefs, beliefs become reality — and how to soften both.',
    category: 'soul-thoughts',
    date: '2022-07-01',
    image:
      'https://images.unsplash.com/photo-1469474968028-56623f02e42e?auto=format&fit=crop&q=80&w=1200',
    externalUrl:
      'https://milangelassoulgarden.com/2022/07/01/the-power-of-thought/',
    content: [
      {
        type: 'paragraph',
        text: 'Probably one of the most powerful tools man has. And yet so poorly understood, or so blithely disregarded.',
      },
      {
        type: 'paragraph',
        text: 'Everything man has ever created was first in the mind. From basic ideas to great inventions. That a writer first conjures the story up and then puts it on paper is simple to understand! Really logical. But do we understand what that means? Thinking about the power of thought usually ends here, with the finished project. Once the work is done and the product sees the light of day, we no longer attribute any power to thoughts. As if we froze our ability to creatively think and to have things materialize in physical form until the next project.',
      },
      {
        type: 'paragraph',
        text: 'Supposedly, humans have about 70,000 thoughts a day, mostly at a subconscious level, but they all affect us. Our lives, the way we think, our habits, our opinions, how we look, who we surround ourselves with, how we breathe, and who we like and/or dislike. Whether we believe it or not is utterly irrelevant.',
      },
    ],
  },

  // Energy Tuning & Meditation
  {
    slug: 'old-habits-die-hard',
    title: 'Old habits die hard',
    excerpt:
      'Why patterns persist, and how energy work meets the body where conscious effort cannot. A look at the deeper layers of change through Biofield Tuning.',
    category: 'energy-tuning',
    date: '2022-08-20',
    image:
      'https://images.unsplash.com/photo-1528319725582-ddc096101511?auto=format&fit=crop&q=80&w=1200',
    externalUrl:
      'https://milangelassoulgarden.com/2022/08/20/old-habits-die-hard/',
    content: [
      { type: 'heading', text: 'A little something about epigenetics' },
      {
        type: 'paragraph',
        text: 'People are different. Some are calm, dreamy, understanding, and patient. Others are angry, impatient, irritable, hostile, toxic, and irrational. Some live in their own world. The list of traits is endless.',
      },
      {
        type: 'paragraph',
        text: "When something throws us off we react differently. Behavioral patterns turn into habits and we stick to them. The problem is that these reactions get intertwined and become a way of life. Some people enjoy life's adventures and travel a lot, the world is their oyster. The sulky and surly always find a reason to be angry. And then the sadness personified. Those who feel everyone blames them for whatever happens. Those that get the short end of the stick. An easy target.",
      },
      {
        type: 'paragraph',
        text: 'The belief that our life is the reflection of our “thoughts and (re) actions” is getting more and more attention. Undoubtedly, a hard nut to crack. Our life takes on a form we must experience in order for us to grow spiritually.',
      },
    ],
  },
]
